import type { HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Chip } from "@/components/ui/Chip";

// Card primitive. The base `<Card>` API is widened from the original
// passthrough to match TB-EMS's Card — `tone` / `interactive` / `accent` /
// `header` / `actions` / `padding` — but every new prop is OPTIONAL and a
// bare `<Card>` (or `<Card className="p-4">`) renders exactly as before, so
// the ~95 existing call sites need no changes.
//
// `padding` intentionally defaults to *unset* (TB-EMS defaults to p-3):
// most operator-spa call sites pass their own p-4/p-5, and a default would
// double up.

export type CardTone =
  | "default"
  | "import"
  | "export"
  | "alarm-critical"
  | "alarm-major"
  | "alarm-minor"
  | "alarm-warning"
  | "live"
  | "brand"
  | "muted";

const TONE_COLOR: Record<CardTone, string | null> = {
  default: null,
  // Grid-direction tones — the canonical SCADA pair (DSGN-05): direction
  // words always wear direction colours, never ok/warn status colours.
  import: "var(--ems-grid-import)",
  export: "var(--ems-grid-export)",
  "alarm-critical": "var(--ems-alarm-critical)",
  "alarm-major": "var(--ems-alarm-major)",
  "alarm-minor": "var(--ems-alarm-minor)",
  "alarm-warning": "var(--ems-alarm-warning)",
  live: "var(--ems-ok)",
  brand: "var(--ems-brand)",
  muted: "var(--ems-rule-strong)",
};

const PADDING_CLASS = {
  none: "",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
} as const;

type CardProps = HTMLAttributes<HTMLDivElement> & {
  tone?: CardTone;
  /** Hover lift + pointer cursor. */
  interactive?: boolean;
  /** CSS color overriding the tone-derived left accent strip. */
  accent?: string;
  /** Top region rendered above a 1px rule. */
  header?: ReactNode;
  /** Right-aligned cluster on the header row. */
  actions?: ReactNode;
  /** Inner padding preset. Unset by default (callers may pass their own). */
  padding?: keyof typeof PADDING_CLASS;
};

export function Card({
  tone = "default",
  interactive = false,
  accent,
  header,
  actions,
  padding,
  className,
  children,
  ...rest
}: CardProps) {
  const accentColor = accent ?? TONE_COLOR[tone];
  return (
    <div
      className={cn(
        "card",
        accentColor && "relative",
        interactive && "card-hover cursor-pointer",
        padding && PADDING_CLASS[padding],
        className,
      )}
      {...rest}
    >
      {accentColor && (
        <span
          aria-hidden
          className="absolute bottom-0 left-0 top-0 w-[3px] rounded-l-xl"
          style={{ background: accentColor }}
        />
      )}
      {(header || actions) && (
        <div
          className={cn(
            "flex items-center justify-between gap-2",
            padding ? "-mx-1 -mt-1 mb-2 pb-2" : "px-4 py-2.5",
          )}
          style={{ borderBottom: "1px solid var(--ems-rule)" }}
        >
          <div className="min-w-0 flex-1">{header}</div>
          {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function CardHeader({ className, children }: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "flex items-center justify-between border-b border-edge bg-surface-2/40 px-4 py-2.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

// Calm, sentence-case card header — matches TB-EMS's restrained chrome
// (the prior uppercase + letter-spacing "shouting" treatment is dropped).
export function CardTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-text-muted">{children}</span>
      {hint}
    </div>
  );
}

// GranBadge is now a thin Chip wrapper — keeps its 14 call sites unchanged
// while retiring the standalone `.gran-badge` CSS.
export function GranBadge({
  live = false,
  label,
  stale = false,
}: {
  live?: boolean;
  label: string;
  stale?: boolean;
}) {
  return (
    <Chip tone={live ? "live" : stale ? "stale" : "neutral"} size="xs">
      {label}
    </Chip>
  );
}
